"use client"

import { enemyTypes } from "@/data/enemy-types"
import type { EnemyVariant } from "@/types/enemy.types"
import { ENEMY_SPRITESHEET, ENEMY_SPRITE_COUNT, ENEMY_SPRITE_INDEX } from "@/components/game-ui/enemy-sprite-index"
import { spriteStripStyle } from "@/components/game-ui/sprite-strip"



interface NewEnemyCardProps {
  enemyId: string
  variant: EnemyVariant
  isOpen: boolean
  onClose: () => void
}

// Helper functions to convert stats to simplified descriptions
const getHealthLabel = (health: number): string => {
  if (health < 75) return "Low Health"
  if (health < 150) return "Average Health"
  if (health < 300) return "High Health"
  return "Very High Health"
}

const getSpeedLabel = (speed: number): string => {
  if (speed < 1.5) return "Slow"
  if (speed < 2.0) return "Average Speed"
  if (speed < 2.5) return "Fast"
  return "Very Fast"
}

const getDamageLabel = (damage: { min: number; max: number }): string => {
  const avg = (damage.min + damage.max) / 2
  if (avg < 8) return "Low Damage"
  if (avg < 15) return "Average Damage"
  if (avg < 25) return "High Damage"
  return "Very High Damage"
}

export const NewEnemyCard = ({ enemyId, variant, isOpen, onClose }: NewEnemyCardProps) => {
  if (!isOpen) return null

  const enemy = enemyTypes[enemyId]
  if (!enemy) return null

  const spriteIndex = ENEMY_SPRITE_INDEX[enemyId]
  const hasSprite = spriteIndex !== undefined

  // Get the appropriate display name based on variant
  const displayName =
    variant === "unique"
      ? enemy.variants?.unique?.displayName || enemy.displayName
      : variant === "elite"
      ? enemy.variants?.elite?.displayName || enemy.displayName
      : enemy.displayName

  // Get simplified stats
  const healthLabel = getHealthLabel(enemy.stats.health)
  const speedLabel = getSpeedLabel(enemy.stats.speed)
  const damageLabel = getDamageLabel(enemy.stats.damage)

  // Card title based on variant
  const cardTitle =
    variant === "unique" ? "NEW BOSS" : variant === "elite" ? "NEW ELITE" : "NEW ENEMY"

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-[100] backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Card Modal */}
      <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
        <div
          className="relative w-full max-w-2xl pointer-events-auto animate-in zoom-in-95 duration-300"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="h-12 flex items-center justify-center rounded-t-lg shadow-md"
            style={{
              background: "linear-gradient(to bottom, #6b3410, #4a2410)",
            }}
          >
            <h1
              className="text-xl md:text-2xl font-bold italic tracking-wide"
              style={{
                color: "#f5e6d3",
                textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
              }}
            >
              {cardTitle}
            </h1>
          </div>

          {/* Parchment Content */}
          <div
            className="relative rounded-b-lg shadow-2xl overflow-hidden"
            style={{
              background: "linear-gradient(to bottom, #f5e6d3, #e8d5b7)",
              borderLeft: "3px solid #c9a66b",
              borderRight: "3px solid #c9a66b",
              borderBottom: "3px solid #c9a66b",
            }}
          >
            {/* Content Area */}
            <div className="py-8 px-6 md:px-8 min-h-[300px]">
              <div className="flex flex-col md:flex-row gap-6 items-start">
                {/* Left side - Tilted Polaroid with enemy portrait */}
                <div
                  className="shrink-0 transform -rotate-6"
                  style={{
                    background: "white",
                    padding: "12px",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
                  }}
                >
                  <div className="w-32 h-32 bg-slate-700 relative">
                    {hasSprite ? (
                      <div
                        className="w-full h-full"
                        style={{
                          backgroundImage: `url(${ENEMY_SPRITESHEET})`,
                          ...spriteStripStyle(spriteIndex, ENEMY_SPRITE_COUNT),
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-4xl font-bold text-amber-400">
                        ?
                      </div>
                    )}
                  </div>
                </div>

                {/* Right side - Enemy details */}
                <div className="flex-1">
                  {/* Enemy name */}
                  <h2
                    className="text-3xl font-bold italic mb-3"
                    style={{ color: "#2d1810" }}
                  >
                    {displayName}
                  </h2>

                  {/* Description */}
                  <p
                    className="text-base italic mb-4 leading-relaxed"
                    style={{ color: "#2d1810" }}
                  >
                    {enemy.description}
                  </p>

                  {/* Stats */}
                  <div className="space-y-1.5">
                    <div
                      className="flex items-center gap-2 text-sm font-medium italic"
                      style={{ color: "#5a3a1a" }}
                    >
                      <span>•</span>
                      <span>{healthLabel}</span>
                    </div>
                    <div
                      className="flex items-center gap-2 text-sm font-medium italic"
                      style={{ color: "#5a3a1a" }}
                    >
                      <span>•</span>
                      <span>{speedLabel}</span>
                    </div>
                    <div
                      className="flex items-center gap-2 text-sm font-medium italic"
                      style={{ color: "#5a3a1a" }}
                    >
                      <span>•</span>
                      <span>{damageLabel}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Ok button - sticking out below */}
            <div className="flex justify-center pb-4 px-4">
              <button
                onClick={onClose}
                className="px-12 py-3 rounded-lg font-bold italic text-lg transition-all hover:brightness-110 active:scale-95 shadow-md"
                style={{
                  background: "linear-gradient(to bottom, #3b82f6, #2563eb)",
                  color: "#ffffff",
                  textShadow: "1px 1px 2px rgba(0,0,0,0.5)",
                }}
              >
                Ok!
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
